/**
 * Edit Company Modal Component
 * Modal for MEMBER to submit update request for owned company
 * Only allows editing certain fields (not nameTh, provinceId, etc.)
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { FiX, FiSave, FiAlertCircle, FiUpload } from 'react-icons/fi';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { apiClient } from '../../services/api';
import { useServiceTypes } from '../../hooks/useServiceTypes';
import MapPicker from '../maps/MapPicker';
import MemberGalleryManager from './MemberGalleryManager';
import { IMAGE_LIMITS, IMAGE_ERROR_MESSAGES } from '../../constants/limits';

interface Province {
  id: string;
  nameTh: string;
  nameEn: string;
}

interface Company {
  id: string;
  nameTh: string;
  nameEn?: string;
  description: string;
  phone1: string;
  phone2?: string;
  email: string;
  lineId?: string;
  website?: string;
  address?: string;
  provinceId: string;
  postalCode?: string;
  companySize?: string;
  establishedYear?: number;
  licenseNumber?: string;
  taxId?: string;
  serviceTypes?: string[];
  serviceAreas?: string[];
  logoUrl?: string;
  cloudinaryPublicId?: string;
  latitude?: number;
  longitude?: number;
  galleryImages?: Array<{
    id: string;
    imageUrl: string;
    caption?: string;
    altText?: string;
    displayOrder: number;
  }>;
}

interface EditCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  company: Company;
  onSuccess?: () => void;
}

const COMPANY_SIZE_OPTIONS = [
  { value: 'SMALL', label: 'เล็ก (1-50 คน)' },
  { value: 'MEDIUM', label: 'กลาง (51-200 คน)' },
  { value: 'LARGE', label: 'ใหญ่ (201-500 คน)' },
  { value: 'ENTERPRISE', label: 'องค์กรขนาดใหญ่ (500+ คน)' }
];

const EditCompanyModal: React.FC<EditCompanyModalProps> = ({
  isOpen,
  onClose,
  company,
  onSuccess
}) => {
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [provincesLoading, setProvincesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch service types from API
  const { serviceTypes, loading: serviceTypesLoading, error: serviceTypesError } = useServiceTypes(true);

  // Logo file state
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');

  const [formData, setFormData] = useState({
    nameEn: company.nameEn || '',
    description: company.description || '',
    phone1: company.phone1 || '',
    phone2: company.phone2 || '',
    email: company.email || '',
    lineId: company.lineId || '',
    website: company.website || '',
    address: company.address || '',
    postalCode: company.postalCode || '',
    companySize: company.companySize || '',
    establishedYear: company.establishedYear?.toString() || '',
    licenseNumber: company.licenseNumber || '',
    serviceTypes: company.serviceTypes || [],
    serviceAreas: company.serviceAreas || [],
    latitude: company.latitude?.toString() || '13.7563',  // Default: Bangkok coordinates
    longitude: company.longitude?.toString() || '100.5018',  // Default: Bangkok coordinates
    userNotes: ''
  });

  // Load provinces from API
  useEffect(() => {
    const fetchProvinces = async () => {
      if (!isOpen) return;

      try {
        setProvincesLoading(true);
        const response = await apiClient.get('/api/provinces/all-details');
        const provincesData = response.data.data.provinces;
        setProvinces(provincesData);
      } catch (error) {
        console.error('Failed to fetch provinces:', error);
        toast.error('ไม่สามารถโหลดข้อมูลจังหวัดได้');
      } finally {
        setProvincesLoading(false);
      }
    };

    fetchProvinces();
  }, [isOpen]);

  // Reset form when modal opens with new company
  useEffect(() => {
    if (isOpen) {
      // Convert serviceType names (from API) to IDs (for checkboxes)
      const serviceTypeIds = (company.serviceTypes || []).map(name => {
        const found = serviceTypes.find(st => st.nameTh === name);
        return found ? found.id : name; // fallback to name if not found (in case it's already an ID)
      }).filter(Boolean);

      // Convert province names (from API) to IDs (for checkboxes)
      const provinceIds = (company.serviceAreas || []).map(name => {
        const found = provinces.find(p => p.nameTh === name);
        return found ? found.id : name; // fallback to name if not found (in case it's already an ID)
      }).filter(Boolean);

      setFormData({
        nameEn: company.nameEn || '',
        description: company.description || '',
        phone1: company.phone1 || '',
        phone2: company.phone2 || '',
        email: company.email || '',
        lineId: company.lineId || '',
        website: company.website || '',
        address: company.address || '',
        postalCode: company.postalCode || '',
        companySize: company.companySize || '',
        establishedYear: company.establishedYear?.toString() || '',
        licenseNumber: company.licenseNumber || '',
        serviceTypes: serviceTypeIds,
        serviceAreas: provinceIds,
        latitude: company.latitude?.toString() || '13.7563',  // Default: Bangkok coordinates
        longitude: company.longitude?.toString() || '100.5018',  // Default: Bangkok coordinates
        userNotes: ''
      });
      setErrors({});
      // Reset logo preview
      setLogoFile(null);
      setLogoPreview('');
    }
  }, [isOpen, company, serviceTypes, provinces]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('กรุณาเลือกไฟล์รูปภาพเท่านั้น');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('ขนาดไฟล์ต้องไม่เกิน 5MB');
        return;
      }

      setLogoFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview('');
  };

  const handleMapLocationSelect = (lat: number, lng: number) => {
    setFormData(prev => ({
      ...prev,
      latitude: lat.toString(),
      longitude: lng.toString()
    }));
  };

  const handleServiceTypeToggle = (serviceTypeId: string) => {
    setFormData(prev => ({
      ...prev,
      serviceTypes: prev.serviceTypes.includes(serviceTypeId)
        ? prev.serviceTypes.filter(t => t !== serviceTypeId)
        : [...prev.serviceTypes, serviceTypeId]
    }));
  };

  const handleServiceAreaToggle = (provinceId: string) => {
    setFormData(prev => ({
      ...prev,
      serviceAreas: prev.serviceAreas.includes(provinceId)
        ? prev.serviceAreas.filter(p => p !== provinceId)
        : [...prev.serviceAreas, provinceId]
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.description.trim()) newErrors.description = 'กรุณากรอกรายละเอียดบริษัท';
    if (!formData.phone1.trim()) newErrors.phone1 = 'กรุณากรอกเบอร์โทรศัพท์';
    if (!formData.email.trim()) newErrors.email = 'กรุณากรอกอีเมล';

    // Email format
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'รูปแบบอีเมลไม่ถูกต้อง';
    }

    // Phone format
    if (formData.phone1 && !/^0\d{1,2}-?\d{3,4}-?\d{4}$/.test(formData.phone1)) {
      newErrors.phone1 = 'รูปแบบเบอร์โทรศัพท์ไม่ถูกต้อง';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Build requestedData with only changed fields
      const requestedData: any = {};
      const changes: any = {};

      // Check each field for changes
      if (formData.nameEn.trim() !== (company.nameEn || '')) {
        requestedData.nameEn = formData.nameEn.trim();
        changes.nameEn = { old: company.nameEn, new: formData.nameEn.trim() };
      }
      if (formData.description.trim() !== company.description) {
        requestedData.description = formData.description.trim();
        changes.description = { old: company.description, new: formData.description.trim() };
      }
      if (formData.phone1.trim() !== company.phone1) {
        requestedData.phone1 = formData.phone1.trim();
        changes.phone1 = { old: company.phone1, new: formData.phone1.trim() };
      }
      if (formData.phone2.trim() !== (company.phone2 || '')) {
        requestedData.phone2 = formData.phone2.trim();
        changes.phone2 = { old: company.phone2, new: formData.phone2.trim() };
      }
      if (formData.email.trim() !== company.email) {
        requestedData.email = formData.email.trim();
        changes.email = { old: company.email, new: formData.email.trim() };
      }
      if (formData.lineId.trim() !== (company.lineId || '')) {
        requestedData.lineId = formData.lineId.trim();
        changes.lineId = { old: company.lineId, new: formData.lineId.trim() };
      }
      if (formData.website.trim() !== (company.website || '')) {
        requestedData.website = formData.website.trim();
        changes.website = { old: company.website, new: formData.website.trim() };
      }
      if (formData.address.trim() !== (company.address || '')) {
        requestedData.address = formData.address.trim();
        changes.address = { old: company.address, new: formData.address.trim() };
      }
      if (formData.postalCode.trim() !== (company.postalCode || '')) {
        requestedData.postalCode = formData.postalCode.trim();
        changes.postalCode = { old: company.postalCode, new: formData.postalCode.trim() };
      }
      if (formData.companySize !== (company.companySize || '')) {
        requestedData.companySize = formData.companySize;
        changes.companySize = { old: company.companySize, new: formData.companySize };
      }
      if (formData.establishedYear.trim()) {
        const year = parseInt(formData.establishedYear);
        if (year !== company.establishedYear) {
          requestedData.establishedYear = year;
          changes.establishedYear = { old: company.establishedYear, new: year };
        }
      }
      if (formData.licenseNumber.trim() !== (company.licenseNumber || '')) {
        requestedData.licenseNumber = formData.licenseNumber.trim();
        changes.licenseNumber = { old: company.licenseNumber, new: formData.licenseNumber.trim() };
      }
      if (JSON.stringify(formData.serviceTypes) !== JSON.stringify(company.serviceTypes || [])) {
        // Convert IDs back to names for backend
        const serviceTypeNames = formData.serviceTypes.map(id => {
          const found = serviceTypes.find(st => st.id === id);
          return found ? found.nameTh : id;
        });
        requestedData.serviceTypes = serviceTypeNames;
        changes.serviceTypes = { old: company.serviceTypes, new: serviceTypeNames };
      }
      if (JSON.stringify(formData.serviceAreas) !== JSON.stringify(company.serviceAreas || [])) {
        // Convert IDs back to names for backend
        const provinceNames = formData.serviceAreas.map(id => {
          const found = provinces.find(p => p.id === id);
          return found ? found.nameTh : id;
        });
        requestedData.serviceAreas = provinceNames;
        changes.serviceAreas = { old: company.serviceAreas, new: provinceNames };
      }

      // Logo changes
      if (logoFile) {
        requestedData._hasLogoUpload = true; // Flag for backend
        changes.logo = { old: company.logoUrl, new: 'new_upload' };
      }

      // GPS changes
      if (formData.latitude && formData.longitude) {
        const newLat = parseFloat(formData.latitude);
        const newLng = parseFloat(formData.longitude);

        // Validate latitude and longitude ranges
        if (newLat < -90 || newLat > 90) {
          toast.error('ละติจูดต้องอยู่ระหว่าง -90 ถึง 90');
          setLoading(false);
          return;
        }
        if (newLng < -180 || newLng > 180) {
          toast.error('ลองจิจูดต้องอยู่ระหว่าง -180 ถึง 180');
          setLoading(false);
          return;
        }

        if (newLat !== company.latitude || newLng !== company.longitude) {
          requestedData.latitude = newLat;
          requestedData.longitude = newLng;
          changes.latitude = { old: company.latitude, new: newLat };
          changes.longitude = { old: company.longitude, new: newLng };
        }
      }

      // Check if any changes
      if (Object.keys(requestedData).length === 0 && !logoFile) {
        toast('ไม่มีข้อมูลที่เปลี่ยนแปลง');
        setLoading(false);
        return;
      }

      // Submit update request
      if (logoFile) {
        // Use FormData for logo upload
        const formDataToSend = new FormData();
        formDataToSend.append('requestedData', JSON.stringify(requestedData));
        formDataToSend.append('logo', logoFile);

        if (formData.userNotes.trim()) {
          formDataToSend.append('userNotes', formData.userNotes.trim());
        }

        // Don't set Content-Type manually - browser will set it with boundary automatically
        await apiClient.post(`/api/change-requests/companies/${company.id}/update`, formDataToSend);
      } else {
        // Regular JSON submission
        await apiClient.post(`/api/change-requests/companies/${company.id}/update`, {
          requestedData,
          userNotes: formData.userNotes.trim() || undefined
        });
      }

      // Success
      toast.success('ส่งคำขอแก้ไขข้อมูลสำเร็จ รอผู้ดูแลระบบอนุมัติ');
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Failed to create update request:', error);

      // Check for gallery limit error from backend
      const errorDetail = error.response?.data?.detail;
      if (errorDetail && typeof errorDetail === 'string' && errorDetail.includes('gallery')) {
        toast.error(IMAGE_ERROR_MESSAGES.BACKEND_REJECTION + ` (สูงสุด ${IMAGE_LIMITS.GALLERY_MAX} รูป)`);
      } else {
        toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาดในการส่งคำขอ');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="flex min-h-screen items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-full max-w-4xl"
          >
            <Card className="p-6 max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">แก้ไขข้อมูลบริษัท</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {company.nameTh}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={loading}
                >
                  <FiX className="w-6 h-6" />
                </button>
              </div>

              {/* Info Alert */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start space-x-3">
                <FiAlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-sm text-blue-800">
                  <p className="font-semibold mb-1">หมายเหตุ</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>การแก้ไขข้อมูลต้องผ่านการอนุมัติจากผู้ดูแลระบบ</li>
                    <li>ไม่สามารถแก้ไข: ชื่อบริษัท (ไทย), จังหวัด, เลขประจำตัวผู้เสียภาษี</li>
                    <li>คุณสามารถติดตามสถานะคำขอได้ที่หน้า "คำขอของฉัน"</li>
                  </ul>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name (Read-only) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ชื่อบริษัท (ภาษาไทย) - ไม่สามารถแก้ไขได้
                  </label>
                  <input
                    type="text"
                    value={company.nameTh}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                  />
                </div>

                {/* English Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ชื่อบริษัท (ภาษาอังกฤษ)
                  </label>
                  <input
                    type="text"
                    value={formData.nameEn}
                    onChange={(e) => handleChange('nameEn', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. Security Company Co., Ltd."
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    รายละเอียดบริษัท <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    rows={4}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                      errors.description ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.description && <p className="mt-1 text-sm text-red-500">{errors.description}</p>}
                </div>

                {/* Logo Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    โลโก้บริษัท
                  </label>

                  {/* Existing Logo */}
                  {company.logoUrl && !logoPreview && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-600 mb-2">โลโก้ปัจจุบัน:</p>
                      <img
                        src={company.logoUrl}
                        alt="Current Logo"
                        className="w-24 h-24 object-cover rounded border-2 border-gray-300"
                      />
                    </div>
                  )}

                  {/* Logo Preview */}
                  {logoPreview && (
                    <div className="flex items-start space-x-4 mb-3">
                      <div className="relative">
                        <img
                          src={logoPreview}
                          alt="Logo Preview"
                          className="w-24 h-24 object-cover rounded border-2 border-blue-500"
                        />
                        <button
                          type="button"
                          onClick={handleRemoveLogo}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                        >
                          <FiX className="w-3 h-3" />
                        </button>
                      </div>
                      <div>
                        <p className="text-sm text-gray-700 font-medium">โลโก้ใหม่</p>
                        <p className="text-xs text-gray-500">{logoFile?.name}</p>
                        <p className="text-xs text-gray-500">
                          {logoFile ? `${(logoFile.size / 1024).toFixed(0)} KB` : ''}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* File Input */}
                  {!logoPreview && (
                    <label className="block cursor-pointer">
                      <div className="flex items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 transition-colors">
                        <div className="text-center">
                          <FiUpload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                          <span className="text-xs text-gray-600">อัปโหลดโลโก้ใหม่</span>
                          <p className="text-xs text-gray-500 mt-1">PNG, JPG ไม่เกิน 5MB</p>
                        </div>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={handleLogoFileChange}
                      />
                    </label>
                  )}
                </div>

                {/* Gallery Images Section - Using MemberGalleryManager */}
                <div>
                  <MemberGalleryManager
                    companyId={company.id}
                    existingImages={company.galleryImages || []}
                    onRefresh={() => {
                      // Optionally refresh company data or just show success message
                      console.log('Gallery updated, refresh company data if needed');
                    }}
                    maxImages={6}
                  />
                </div>

                {/* Contact */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      เบอร์โทรศัพท์ 1 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      value={formData.phone1}
                      onChange={(e) => handleChange('phone1', e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                        errors.phone1 ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.phone1 && <p className="mt-1 text-sm text-red-500">{errors.phone1}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      เบอร์โทรศัพท์ 2
                    </label>
                    <input
                      type="tel"
                      value={formData.phone2}
                      onChange={(e) => handleChange('phone2', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      อีเมล <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                        errors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Line ID
                    </label>
                    <input
                      type="text"
                      value={formData.lineId}
                      onChange={(e) => handleChange('lineId', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      เว็บไซต์
                    </label>
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) => handleChange('website', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ที่อยู่
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Postal Code & License Number */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      รหัสไปรษณีย์
                    </label>
                    <input
                      type="text"
                      value={formData.postalCode}
                      onChange={(e) => handleChange('postalCode', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="10110"
                      maxLength={5}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      เลขที่ใบอนุญาต
                    </label>
                    <input
                      type="text"
                      value={formData.licenseNumber}
                      onChange={(e) => handleChange('licenseNumber', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="123456789"
                    />
                  </div>
                </div>

                {/* GPS Location */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    พิกัด GPS (ทำเลที่ตั้ง)
                  </label>

                  {/* Manual GPS Input */}
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <input
                        type="number"
                        step="any"
                        value={formData.latitude}
                        onChange={(e) => handleChange('latitude', e.target.value)}
                        placeholder="ละติจูด (Latitude)"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <input
                        type="number"
                        step="any"
                        value={formData.longitude}
                        onChange={(e) => handleChange('longitude', e.target.value)}
                        placeholder="ลองจิจูด (Longitude)"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* Map Picker */}
                  {(formData.latitude && formData.longitude) && (
                    <div className="rounded-lg overflow-hidden border border-gray-300">
                      <MapPicker
                        latitude={parseFloat(formData.latitude) || 13.7563}
                        longitude={parseFloat(formData.longitude) || 100.5018}
                        onLocationSelect={handleMapLocationSelect}
                        height="300px"
                      />
                    </div>
                  )}

                  {/* Help Text */}
                  {(!formData.latitude || !formData.longitude) && (
                    <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded p-2">
                      <p className="font-medium mb-1">วิธีกรอกพิกัด:</p>
                      <ul className="list-disc list-inside space-y-0.5">
                        <li>กรอกพิกัดด้วยตนเอง แล้วคลิกบนแผนที่เพื่อปรับตำแหน่ง</li>
                        <li>พิกัด GPS ช่วยให้ผู้ใช้หาบริษัทตามระยะทางได้</li>
                      </ul>
                    </div>
                  )}
                </div>

                {/* Company Size & Year */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ขนาดบริษัท
                    </label>
                    <select
                      value={formData.companySize}
                      onChange={(e) => handleChange('companySize', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">เลือกขนาดบริษัท</option>
                      {COMPANY_SIZE_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ปีที่ก่อตั้ง
                    </label>
                    <input
                      type="number"
                      value={formData.establishedYear}
                      onChange={(e) => handleChange('establishedYear', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      min="1900"
                      max={new Date().getFullYear()}
                    />
                  </div>
                </div>

                {/* Service Types */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ประเภทบริการ
                  </label>

                  {serviceTypesError && (
                    <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded flex items-start space-x-2">
                      <FiAlertCircle className="text-red-600 flex-shrink-0 mt-0.5 h-4 w-4" />
                      <p className="text-xs text-red-800">{serviceTypesError}</p>
                    </div>
                  )}

                  {serviceTypesLoading ? (
                    <div className="flex items-center justify-center py-6">
                      <div className="flex items-center space-x-2">
                        <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-sm text-gray-600">กำลังโหลด...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {serviceTypes.map(serviceType => (
                        <label
                          key={serviceType.id}
                          className="flex items-center space-x-2 p-2 border border-gray-300 rounded cursor-pointer hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            checked={formData.serviceTypes.includes(serviceType.id)}
                            onChange={() => handleServiceTypeToggle(serviceType.id)}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <span className="text-sm">{serviceType.nameTh}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Service Areas */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    พื้นที่บริการ
                  </label>

                  {provincesLoading ? (
                    <div className="flex items-center justify-center py-6 border border-gray-200 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-sm text-gray-600">กำลังโหลดจังหวัด...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                      {provinces.map(province => (
                        <label
                          key={province.id}
                          className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                        >
                          <input
                            type="checkbox"
                            checked={formData.serviceAreas.includes(province.id)}
                            onChange={() => handleServiceAreaToggle(province.id)}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <span className="text-sm">{province.nameTh}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* User Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    หมายเหตุถึงผู้ดูแลระบบ
                  </label>
                  <textarea
                    value={formData.userNotes}
                    onChange={(e) => handleChange('userNotes', e.target.value)}
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="อธิบายเหตุผลในการแก้ไขข้อมูล..."
                    maxLength={1000}
                  />
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    disabled={loading}
                  >
                    ยกเลิก
                  </Button>
                  <Button
                    type="submit"
                    variant="default"
                    disabled={loading}
                    leftIcon={<FiSave className="w-4 h-4" />}
                  >
                    {loading ? 'กำลังส่งคำขอ...' : 'ส่งคำขอแก้ไข'}
                  </Button>
                </div>
              </form>
            </Card>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
};

export default EditCompanyModal;
